/**
 * Yönetici Sınav Detay — Spec Bölüm 7.1
 *
 * - Erişim kodu
 * - Davet linki (kopyalanabilir)
 * - Süre/soru/anonim mod
 * - Aday katılım sayısı (S2'de sadece sayı; rapor S4'te)
 * - Followup oluştur butonu (Delta için, S4 sonu)
 */
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'wouter';
import { Header } from '../components/Header.js';
import { Alert, Button, Card } from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/auth-client.js';
import { trackEngagement } from '../lib/engagement.js';

interface ExamDetail {
  id: string;
  accessCode: string;
  durationMin: number;
  questionCount: number;
  anonymousMode: boolean;
  isActive: boolean;
  validFrom: string;
  validUntil: string;
  createdAt: string;
  templateId: string | null;
  previousExamId: string | null;
  _count: { attempts: number };
}

export function ExamDetailPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const { data: session, isPending: sessionPending } = useSession();
  const params = useParams<{ id: string }>();
  const examId = params.id ?? '';
  const [exam, setExam] = useState<ExamDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (sessionPending) return;
    if (!session?.user) {
      setLocation('/yonetici/giris');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<ExamDetail>(`/api/manager/exams/${examId}`);
        if (!cancelled) setExam(data);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setError(err.message);
        else setError('Sınav yüklenemedi');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [examId, session, sessionPending, setLocation]);

  if (sessionPending || (!exam && !error)) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-3xl px-6 py-12">
          <p className="text-slate-500">Yükleniyor…</p>
        </main>
      </div>
    );
  }

  if (error || !exam) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-3xl px-6 py-12">
          <Alert variant="danger">{error ?? 'Sınav bulunamadı'}</Alert>
          <Button
            variant="secondary"
            onClick={() => setLocation('/yonetici/dashboard')}
            className="mt-4"
          >
            Panele Dön
          </Button>
        </main>
      </div>
    );
  }

  const examIdSafe = exam.id;
  const inviteUrl = `${window.location.origin}/sinav?code=${exam.accessCode}`;
  const validUntilDate = new Date(exam.validUntil);
  const daysLeft = Math.max(
    0,
    Math.ceil((validUntilDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000)),
  );

  async function copyInvite(): Promise<void> {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      trackEngagement('invite_link_copied', { examId: examIdSafe });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard erişimi yok — manuel kopyalama
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-3xl px-6 py-12">
        <button
          type="button"
          onClick={() => setLocation('/yonetici/dashboard')}
          className="text-sm font-medium text-blue-900 hover:underline"
        >
          ← Panel
        </button>

        <h1 className="mt-2 text-3xl font-bold text-slate-900">
          Sınav Hazır
        </h1>
        <p className="mt-1 text-slate-600">
          Adaylarınıza aşağıdaki linki veya erişim kodunu paylaşın.
          Bağlantı {daysLeft} gün boyunca geçerli.
        </p>

        {/* Erişim kodu + link kart */}
        <Card className="mt-6">
          <h2 className="text-sm font-medium uppercase tracking-wider text-slate-500">
            Erişim Kodu
          </h2>
          <p className="mt-2 font-mono text-3xl font-bold tracking-wider text-blue-900">
            {exam.accessCode}
          </p>

          <h3 className="mt-6 text-sm font-medium uppercase tracking-wider text-slate-500">
            Davet Linki
          </h3>
          <div className="mt-2 flex items-stretch gap-2">
            <input
              type="text"
              value={inviteUrl}
              readOnly
              onFocus={(e) => e.currentTarget.select()}
              className="flex-1 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm text-slate-700"
            />
            <Button onClick={copyInvite} variant={copied ? 'secondary' : 'primary'} size="md">
              {copied ? 'Kopyalandı ✓' : 'Kopyala'}
            </Button>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Aday bu linke tıklar → erişim kodu otomatik dolar → kayıt → sınav başlar.
          </p>
        </Card>

        {/* Sınav özet metrikler */}
        <div className="mt-6 grid gap-4 sm:grid-cols-4">
          <Metric label="Soru" value={String(exam.questionCount)} />
          <Metric label="Süre" value={`${exam.durationMin} dk`} />
          <Metric
            label="Mod"
            value={exam.anonymousMode ? 'Anonim' : 'Normal'}
          />
          <Metric label="Katılım" value={String(exam._count.attempts)} />
        </div>

        <Card className="mt-6">
          <h3 className="text-base font-semibold text-slate-900">
            Sonraki Adımlar
          </h3>
          <ul className="mt-3 space-y-2 text-sm text-slate-700">
            <li>1. Davet linkini Slack / e-posta / WhatsApp ile adaylara gönderin.</li>
            <li>2. Adaylar sınava katıldıkça bu sayfada katılım sayısı güncellenir.</li>
            <li>
              3. Sınav bittiğinde AI rapor üretimi otomatik başlar (Sprint 4 ilerlemesi).
            </li>
          </ul>
        </Card>

        <div className="mt-8 flex flex-wrap items-center justify-end gap-3">
          <Button
            variant="secondary"
            onClick={async () => {
              if (
                !confirm(
                  'Bu sınavın takip sınavını (delta için) oluşturmak ister misiniz? Aynı şablon ve konfig kullanılır.',
                )
              )
                return;
              try {
                const next = await api.post<{ id: string }>(
                  '/api/manager/exams/create-followup',
                  {
                    previousExamId: exam.id,
                    templateId: exam.templateId,
                    anonymousMode: exam.anonymousMode,
                  },
                );
                setLocation(`/yonetici/sinavlar/${next.id}`);
              } catch (err) {
                alert(
                  err instanceof ApiError
                    ? err.message
                    : 'Followup sınav oluşturulamadı',
                );
              }
            }}
          >
            🔁 Tekrar Sınav (Delta)
          </Button>
          {exam.previousExamId && (
            <Button
              variant="secondary"
              onClick={() => setLocation(`/yonetici/sinavlar/${exam.id}/delta`)}
            >
              Δ Delta Raporu
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() =>
              setLocation(`/yonetici/sinavlar/${exam.id}/adaylar`)
            }
          >
            Adayları Gör ({exam._count.attempts})
          </Button>
          <Button
            onClick={() => setLocation(`/yonetici/sinavlar/${exam.id}/rapor`)}
          >
            Ekip Raporu
          </Button>
        </div>
      </main>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 text-center">
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );
}
